/**
 * Extra-bold bitmap letter masks (# = filled, space = empty).
 * Height 9, thick strokes (~3), tight counters — dense silhouette.
 */

const P = [
  "#########",
  "####..###",
  "###...###",
  "###...###",
  "#########",
  "####.....",
  "###......",
  "###......",
  "###......",
];

const A = [
  "..#####..",
  ".#######.",
  "###...###",
  "###...###",
  "#########",
  "###...###",
  "###...###",
  "###...###",
  "###...###",
];

const S = [
  ".#######.",
  "####..###",
  "###......",
  "####.....",
  ".#######.",
  ".....####",
  "......###",
  "###..####",
  ".#######.",
];

const T = [
  "#########",
  "#########",
  "#########",
  "....###..",
  "....###..",
  "....###..",
  "....###..",
  "....###..",
  "....###..",
];

const I = [
  "#######",
  "#######",
  "..###..",
  "..###..",
  "..###..",
  "..###..",
  "..###..",
  "#######",
  "#######",
];

const R = [
  "#########",
  "####..###",
  "###...###",
  "###...###",
  "#########",
  "####.###.",
  "###..###.",
  "###...###",
  "###...###",
];

const D = [
  "########.",
  "####..###",
  "###...###",
  "###...###",
  "###...###",
  "###...###",
  "###...###",
  "####..###",
  "########.",
];

const MASKS: Record<string, readonly string[]> = {
  p: P,
  a: A,
  s: S,
  t: T,
  i: I,
  r: R,
  d: D,
};

export type MaskCell = {
  row: number;
  col: number;
};

export type WordMask = {
  rows: number;
  cols: number;
  cells: MaskCell[];
};

/** Build a dense mask for a lowercase word (e.g. "pastiradit"). */
export function buildWordMask(
  word: string,
  letterGap = 0,
): WordMask {
  const letters = word.toLowerCase().split("");
  let rows = 0;
  let cols = 0;
  const strips: { mask: readonly string[]; offset: number }[] = [];

  for (const ch of letters) {
    const mask = MASKS[ch];
    if (!mask) continue;
    rows = Math.max(rows, mask.length);
    const w = mask[0]?.length ?? 0;
    strips.push({ mask, offset: cols });
    cols += w + letterGap;
  }
  if (cols > 0 && letterGap > 0) cols -= letterGap;

  const cells: MaskCell[] = [];
  for (const { mask, offset } of strips) {
    for (let r = 0; r < mask.length; r++) {
      const line = mask[r]!;
      for (let c = 0; c < line.length; c++) {
        if (line[c] === "#") {
          cells.push({ row: r, col: offset + c });
        }
      }
    }
  }

  return { rows, cols, cells };
}

/**
 * Stack multiple words vertically, center-aligned.
 * lineGap = empty rows between letter blocks.
 */
export function buildLinesMask(
  lines: string[],
  letterGap = 2,
  lineGap = 2,
): WordMask {
  const built = lines.map((line) => buildWordMask(line, letterGap));
  const cols = built.reduce((max, m) => Math.max(max, m.cols), 0);
  let rows = 0;
  const cells: MaskCell[] = [];

  for (let i = 0; i < built.length; i++) {
    const m = built[i]!;
    const colPad = Math.floor((cols - m.cols) / 2);
    for (const cell of m.cells) {
      cells.push({
        row: rows + cell.row,
        col: colPad + cell.col,
      });
    }
    rows += m.rows;
    if (i < built.length - 1) rows += lineGap;
  }

  return { rows, cols, cells };
}

/** Desktop / wide: single line. */
export const PASTIRADIT_MASK = buildWordMask("pastiradit", 2);

/** Mobile: line 1 "pasti", line 2 "radit". */
export const PASTIRADIT_MASK_MOBILE = buildLinesMask(["pasti", "radit"], 2, 3);
